import React from 'react';

export type PublicationLocator = {
  subjectKind: 'account' | 'strategy';
  accountId?: string | null;
  strategyId?: string | null;
};

export type PublicationPolicy = {
  allowPerformanceView: boolean;
  allowPositionsView: boolean;
  allowFilledTradesView: boolean;
  allowOpenOrdersView: boolean;
  allowExactQuantitiesView: boolean;
  allowCodeView: boolean;
  allowClone: boolean;
};

export type PublicationShareState = {
  publicationId: string | null;
  subject: {
    kind: 'account' | 'strategy';
    name: string;
    accountId: string | null;
    strategyId: string | null;
  };
  marketplace: {
    status: 'not_shared' | 'pending' | 'approved' | 'rejected';
    isShared: boolean;
    listingId: string | null;
    url: string | null;
    title: string | null;
    description: string | null;
    details: string | null;
    heroImageUrl: string | null;
    tags: string[];
    assetClasses: string[];
  };
  privateLink: {
    isActive: boolean;
    url: string | null;
    issuedAt: string | null;
    expiresAt: string | null;
    recipientEmail: string | null;
    passwordRequired: boolean;
  };
  policy: PublicationPolicy;
  strategies: Array<{ id: string; name: string; isPrimary: boolean }>;
  accountChoices: Array<{
    accountId: string;
    displayName: string;
    broker: string;
    mode: string;
  }>;
  selectionRequired: boolean;
  settingsUrl: string | null;
  version: string | null;
};

type PublishMarketplaceBody = {
  subjectKind: 'account' | 'strategy';
  strategyId?: string;
  accountId?: string;
};

type PrivateLinkBody = {
  forceRegenerate: boolean;
  password?: string | null;
  expiresAt?: string | null;
  recipientEmail?: string | null;
};

type JsonError = { message?: string; error?: string };
const NO_TOKEN = async () => null;

async function publicationRequest(
  token: string,
  path: string,
  init?: RequestInit,
): Promise<PublicationShareState> {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${process.env.REACT_APP_API_URL || ''}${path}`, {
    ...init,
    headers,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const failure: JsonError = body;
    throw new Error(
      failure.message || failure.error || `Sharing request failed (${response.status})`,
    );
  }
  return body;
}

export function usePublicationShare(
  locator: PublicationLocator,
  enabled: boolean,
  getApiToken: () => Promise<string | null> = NO_TOKEN,
) {
  const [state, setState] = React.useState<PublicationShareState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState(locator.accountId || '');

  React.useEffect(() => {
    setSelectedAccountId(locator.accountId || '');
  }, [locator.accountId, locator.strategyId]);

  const activeLocator = React.useMemo<PublicationLocator>(
    () => ({
      accountId: selectedAccountId || locator.accountId || null,
      strategyId: locator.strategyId || null,
      subjectKind: locator.subjectKind,
    }),
    [locator.accountId, locator.strategyId, locator.subjectKind, selectedAccountId],
  );

  const refresh = React.useCallback(async () => {
    if (!enabled || (!activeLocator.accountId && !activeLocator.strategyId)) return null;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const query = new URLSearchParams();
      query.set('subjectKind', activeLocator.subjectKind);
      if (activeLocator.accountId) query.set('accountId', activeLocator.accountId);
      if (activeLocator.strategyId) query.set('strategyId', activeLocator.strategyId);
      const next = await publicationRequest(token, `/publications/resolve?${query.toString()}`);
      setState(next);
      const accountChoices = Array.isArray(next.accountChoices) ? next.accountChoices : [];
      if (!selectedAccountId && accountChoices.length === 1) {
        setSelectedAccountId(accountChoices[0].accountId);
      }
      return next;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load sharing settings.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeLocator.accountId, activeLocator.strategyId, enabled, getApiToken, selectedAccountId]);

  React.useEffect(() => {
    if (!enabled) {
      setState(null);
      setError('');
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const ensure = React.useCallback(async () => {
    const token = await getApiToken();
    if (!token) throw new Error('Authentication required');
    const resolveBody: PublishMarketplaceBody = { subjectKind: activeLocator.subjectKind };
    if (activeLocator.accountId) resolveBody.accountId = activeLocator.accountId;
    if (activeLocator.strategyId) resolveBody.strategyId = activeLocator.strategyId;
    const next = await publicationRequest(token, '/publications/resolve', {
      method: 'POST',
      body: JSON.stringify(resolveBody),
    });
    setState(next);
    return next;
  }, [activeLocator.accountId, activeLocator.strategyId, activeLocator.subjectKind, getApiToken]);

  const makePublic = React.useCallback(
    async (options?: { allowClone?: boolean }) => {
      setLoading(true);
      setError('');
      try {
        const current = state?.publicationId ? state : await ensure();
        const publicationId = current.publicationId;
        const strategyId =
          activeLocator.strategyId || current.subject.strategyId || current.strategies[0]?.id;
        if (!publicationId || (activeLocator.subjectKind === 'strategy' && !strategyId)) {
          throw new Error('The selected sharing subject could not be resolved.');
        }
        const token = await getApiToken();
        if (!token) throw new Error('Authentication required');
        if (options?.allowClone !== undefined) {
          const withPolicy = await publicationRequest(
            token,
            `/publications/${publicationId}/settings`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                allowClone: options.allowClone,
                allowCodeView: options.allowClone,
              }),
            },
          );
          setState(withPolicy);
        }
        const body: PublishMarketplaceBody = { subjectKind: activeLocator.subjectKind };
        if (strategyId) body.strategyId = strategyId;
        if (activeLocator.accountId) body.accountId = activeLocator.accountId;

        const next = await publicationRequest(token, `/publications/${publicationId}/marketplace`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        setState(next);
        return {
          success: true,
          isPublic: next.marketplace.isShared,
          marketplaceListingId: next.marketplace.listingId,
          moderationStatus: next.marketplace.status,
        };
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : 'Could not publish.';
        setError(message);
        throw caught;
      } finally {
        setLoading(false);
      }
    },
    [activeLocator.accountId, activeLocator.strategyId, ensure, getApiToken, state],
  );

  const makePrivateLink = React.useCallback(
    async (options?: {
      forceRegenerate?: boolean;
      password?: string | null;
      expiresAt?: string | null;
      recipientEmail?: string | null;
    }) => {
      setLoading(true);
      setError('');
      try {
        const current = state?.publicationId ? state : await ensure();
        if (!current.publicationId) throw new Error('Publication could not be created.');
        const token = await getApiToken();
        if (!token) throw new Error('Authentication required');
        const privateLinkBody: PrivateLinkBody = {
          forceRegenerate: options?.forceRegenerate === true,
        };
        if (options?.password !== undefined) privateLinkBody.password = options.password;
        if (options?.expiresAt !== undefined) privateLinkBody.expiresAt = options.expiresAt;
        if (options?.recipientEmail !== undefined) {
          privateLinkBody.recipientEmail = options.recipientEmail;
        }
        const next = await publicationRequest(
          token,
          `/publications/${current.publicationId}/private-link`,
          {
            method: 'PUT',
            body: JSON.stringify(privateLinkBody),
          },
        );
        setState(next);
        return { success: true, isPrivate: true, shareUrl: next.privateLink.url };
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : 'Could not create private link.';
        setError(message);
        throw caught;
      } finally {
        setLoading(false);
      }
    },
    [ensure, getApiToken, state],
  );

  const removeMarketplace = React.useCallback(async () => {
    if (!state?.publicationId) return false;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(
        token,
        `/publications/${state.publicationId}/marketplace`,
        {
          method: 'DELETE',
        },
      );
      setState(next);
      return true;
    } finally {
      setLoading(false);
    }
  }, [getApiToken, state?.publicationId]);

  const revokePrivateLink = React.useCallback(async () => {
    if (!state?.publicationId) return false;
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(
        token,
        `/publications/${state.publicationId}/private-link`,
        {
          method: 'DELETE',
        },
      );
      setState(next);
      return true;
    } finally {
      setLoading(false);
    }
  }, [getApiToken, state?.publicationId]);

  const updatePolicy = React.useCallback(
    async (policy: Partial<PublicationPolicy>) => {
      const current = state?.publicationId ? state : await ensure();
      if (!current.publicationId) throw new Error('Publication could not be created.');
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const next = await publicationRequest(
        token,
        `/publications/${current.publicationId}/settings`,
        { method: 'PATCH', body: JSON.stringify(policy) },
      );
      setState(next);
      return next;
    },
    [ensure, getApiToken, state],
  );

  const updatePresentation = React.useCallback(
    async (presentation: {
      title?: string | null;
      description?: string | null;
      details?: string | null;
      heroImageUrl?: null;
      tags?: string[];
      assetClasses?: string[];
    }) => {
      const current = state?.publicationId ? state : await ensure();
      if (!current.publicationId) throw new Error('Publication could not be created.');
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const presentationBody = {
        ...presentation,
        expectedVersion: current.version || undefined,
      };
      const next = await publicationRequest(
        token,
        `/publications/${current.publicationId}/presentation`,
        {
          method: 'PATCH',
          body: JSON.stringify(presentationBody),
        },
      );
      setState(next);
      return next;
    },
    [ensure, getApiToken, state],
  );

  const uploadHeroImage = React.useCallback(
    async (file: File, expectedVersion?: string | null) => {
      const current = state?.publicationId ? state : await ensure();
      if (!current.publicationId) throw new Error('Publication could not be created.');
      const token = await getApiToken();
      if (!token) throw new Error('Authentication required');
      const data = new FormData();
      data.append('file', file);
      if (expectedVersion || current.version) {
        data.append('expectedVersion', expectedVersion || current.version || '');
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/publications/${current.publicationId}/hero-image`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: data },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const failure: JsonError = body;
        throw new Error(failure.message || failure.error || 'Image upload failed.');
      }
      const next: PublicationShareState = body;
      setState(next);
      return next;
    },
    [ensure, getApiToken, state],
  );

  return {
    error,
    loading,
    makePrivateLink,
    makePublic,
    refresh,
    removeMarketplace,
    revokePrivateLink,
    selectedAccountId,
    setSelectedAccountId,
    state,
    updatePresentation,
    updatePolicy,
    uploadHeroImage,
  };
}
